/**
 * CloudAccountSelector — dropdown per provider showing connected accounts
 * with "Connect New Account" option and disconnect (X) button.
 */

import { stateful } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';

export interface CloudAccountInfo {
  _id: string;
  provider: 'google_drive' | 'onedrive';
  email: string;
  displayName: string;
  connectedAt: string;
}

export interface CloudAccountSelectorProps {
  provider: 'google_drive' | 'onedrive';
  accounts: CloudAccountInfo[];
  loading: boolean;
  onConnect: () => void;
  onSelect: (account: CloudAccountInfo) => void;
  onDisconnect: (accountId: string) => void;
}

export const CloudAccountSelector = stateful((props: CloudAccountSelectorProps) => {
  const { provider, accounts, loading, onConnect, onSelect, onDisconnect } = props;

  const icon = provider === 'google_drive' ? 'icon-[mdi--google-drive]' : 'icon-[mdi--microsoft-onedrive]';
  const label = provider === 'google_drive' ? 'Google Drive' : 'OneDrive';

  if (loading) {
    return html`
      <button class="btn btn-outline btn-sm gap-2" disabled>
        <span class="loading loading-spinner loading-xs"></span>
        ${label}
      </button>
    `;
  }

  // No accounts → show simple connect button
  if (accounts.length === 0) {
    return html`
      <button class="btn btn-outline btn-sm gap-2" @click=${onConnect}>
        <span class="${icon} text-lg"></span>
        ${label}
      </button>
    `;
  }

  // Has accounts → dropdown
  return html`
    <div class="dropdown dropdown-bottom">
      <div tabindex="0" role="button" class="btn btn-outline btn-sm gap-2">
        <span class="${icon} text-lg"></span>
        ${label}
        <span class="icon-[material-symbols--arrow-drop-down] text-lg -ml-1"></span>
      </div>
      <div tabindex="0" class="dropdown-content z-[1] mt-1 w-80 rounded-lg border border-base-300 bg-base-100 shadow-xl">
        <div class="p-1.5">
          ${accounts.map(
            a => html`
              <div
                class="flex items-center gap-3 rounded-md px-3 py-2.5 cursor-pointer hover:bg-base-200 transition-colors"
                @click=${() => { onSelect(a); (document.activeElement as HTMLElement)?.blur(); }}
              >
                <span class="${icon} text-base-content/40 text-xl flex-none"></span>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium truncate">${a.displayName || a.email}</div>
                  ${a.displayName && a.email !== a.displayName
                    ? html`<div class="text-xs text-base-content/40 truncate">${a.email}</div>`
                    : nothing}
                </div>
                <button
                  class="btn btn-ghost btn-xs btn-square flex-none opacity-40 hover:opacity-100 hover:text-error"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    onDisconnect(a._id);
                    (document.activeElement as HTMLElement)?.blur();
                  }}
                  title="Disconnect account"
                >
                  <span class="icon-[material-symbols--close] text-sm"></span>
                </button>
              </div>
            `,
          )}
        </div>
        <div class="border-t border-base-300">
          <div
            class="flex items-center gap-3 rounded-b-lg px-3 py-2.5 cursor-pointer hover:bg-base-200 transition-colors text-primary"
            @click=${() => { onConnect(); (document.activeElement as HTMLElement)?.blur(); }}
          >
            <span class="icon-[material-symbols--add-circle-outline] text-xl flex-none"></span>
            <span class="text-sm font-medium">Connect new account</span>
          </div>
        </div>
      </div>
    </div>
  `; 
});
